import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct, formatApiErrorDetail } from "@/lib/api";
import { Card, CardHeader } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SymbolLogo } from "@/components/SymbolSearchInput";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import WatchlistAlertButton from "@/components/WatchlistAlertButton";
import BulkAlertButton from "@/components/BulkAlertButton";
import WatchlistImportButton from "@/components/WatchlistImportButton";

export default function WatchlistPage() {
  const [items, setItems] = useState([]);
  const [symbol, setSymbol] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/watchlist");
      setItems(data.items || []);
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!symbol) return;
    try {
      await api.post("/watchlist", { symbol });
      toast.success(`${symbol.toUpperCase()} added`);
      setSymbol("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (s) => {
    await api.delete(`/watchlist/${s}`);
    toast.success(`${s} removed`);
    load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="watchlist-page">
      <div>
        <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / Watchlist</div>
        <h1 className="heading text-4xl font-black tracking-tighter mt-2">Watchlist</h1>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between gap-4">
          <form onSubmit={add} className="flex gap-2 max-w-lg flex-1">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                data-testid="watchlist-symbol-input"
                placeholder="Add ticker (e.g. NVDA)"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                className="pl-9 bg-zinc-950 border-zinc-700 rounded-sm mono h-10"
              />
            </div>
            <Button
              type="submit"
              data-testid="watchlist-add-btn"
              className="bg-white text-black hover:bg-zinc-200 rounded-sm h-10 mono text-xs uppercase tracking-widest gap-1.5"
            >
              <Plus size={14} weight="bold" /> Add
            </Button>
          </form>
          <div className="flex items-center gap-2">
            <WatchlistImportButton onDone={load} />
            {items.length > 0 && <BulkAlertButton onDone={load} />}
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader title={`${items.length} tracked`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] mono uppercase tracking-widest text-zinc-500">
              <tr className="border-b border-zinc-800">
                <th className="text-left px-5 py-2.5">Symbol</th>
                <th className="text-left px-3 py-2.5">5d</th>
                <th className="text-right px-3 py-2.5">Price</th>
                <th className="text-right px-3 py-2.5">Change</th>
                <th className="text-right px-3 py-2.5">Change %</th>
                <th className="text-left px-3 py-2.5">Bias</th>
                <th className="text-right px-5 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="mono">
              {items.map(w => {
                const spark = (w.sparkline || []).map((v, i) => ({ i, v }));
                const trending = spark.length >= 2 && spark[spark.length - 1].v >= spark[0].v;
                const sparkColor = trending ? "#00C805" : "#FF3B30";
                return (
                <tr key={w.symbol} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`watchlist-row-${w.symbol}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <SymbolLogo
                        symbol={w.symbol}
                        logo={`https://financialmodelingprep.com/image-stock/${w.symbol}.png`}
                        size={26}
                      />
                      <div>
                        <div className="font-semibold">{w.symbol}</div>
                        <div className="text-[10px] text-zinc-500 truncate max-w-[240px]">{w.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 w-[140px]">
                    {spark.length >= 2 ? (
                      <div className="h-9 w-[110px]" data-testid={`watchlist-sparkline-${w.symbol}`}>
                        <ResponsiveContainer width={110} height={36}>
                          <LineChart data={spark}>
                            <Line type="monotone" dataKey="v" stroke={sparkColor} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <span className="text-[10px] text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="text-right px-3 py-3">{fmtMoney(w.price)}</td>
                  <td className={`text-right px-3 py-3 ${w.change >= 0 ? "profit" : "loss"}`}>{fmtMoney(w.change, { showSign: true })}</td>
                  <td className={`text-right px-3 py-3 ${w.change_pct >= 0 ? "profit" : "loss"}`}>{fmtPct(w.change_pct)}</td>
                  <td className="px-3 py-3">
                    {w.sentiment ? (
                      <div className="flex items-center gap-1.5" data-testid={`watchlist-sentiment-${w.symbol}`}>
                        <div className="w-16 h-1.5 rounded-sm overflow-hidden bg-zinc-900 border border-zinc-800 flex">
                          <div className="bg-[#00C805]" style={{ width: `${w.sentiment.bullish_pct}%` }} />
                          <div className="bg-zinc-600" style={{ width: `${w.sentiment.neutral_pct}%` }} />
                          <div className="bg-[#FF3B30]" style={{ width: `${w.sentiment.bearish_pct}%` }} />
                        </div>
                        <span className="mono text-[10px]">
                          <span className="text-[#00C805]">{Math.round(w.sentiment.bullish_pct)}</span>
                          <span className="text-zinc-600">/</span>
                          <span className="text-[#FF3B30]">{Math.round(w.sentiment.bearish_pct)}</span>
                        </span>
                      </div>
                    ) : (
                      <span className="text-[10px] text-zinc-600 mono">—</span>
                    )}
                  </td>
                  <td className="text-right px-5 py-3 space-x-3">
                    <WatchlistAlertButton symbol={w.symbol} alert={w.alert} onSaved={load} />
                    <Link to={`/trade?s=${w.symbol}`} className="text-xs text-white hover:text-[#00C805]">Trade</Link>
                    <button onClick={() => remove(w.symbol)} data-testid={`watchlist-remove-${w.symbol}`} className="text-xs text-zinc-500 hover:text-[#FF3B30] inline-flex items-center gap-1">
                      <Trash size={12} /> Remove
                    </button>
                  </td>
                </tr>
              );
              })}
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-16 text-center mono text-xs text-zinc-600">
                  Nothing tracked yet. Add a ticker above.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
